import { FC } from 'react';
import { useHistory } from 'react-router';

import { TeamMemberRole } from '__generated__/GQL';
import { StepProps } from 'components/Stepper/types';
import useToast from 'components/ToastProvider/useToast';
import { MemberReviewLayout } from 'features/Team/components/MemberReviewLayout';
import { useGiveLegalBeneficiaryAccess } from 'features/Team/pages/MembersInvite/hooks/useGiveLegalBeneficiaryAccess';
import { useNewInviteTeamMember } from 'features/Team/pages/MembersInvite/hooks/useNewInviteTeamMember';
import logger from 'helpers/logger';

import * as routes from '../../../../../../routes';
import { useMemberInviteState } from '../../../MemberInviteProvider';
import { getLocalizedError } from '../../utils';
import locales from './locales';

const Review: FC<StepProps> = ({ goBack }) => {
  const history = useHistory();
  const { showToast } = useToast();

  const [
    submitInviteMemberRequest,
    { loading: submitInviteMemberRequestLoading },
  ] = useNewInviteTeamMember();
  const [
    giveLegalBeneficiaryAccess,
    { loading: giveAdminPermissionsRequestLoading },
  ] = useGiveLegalBeneficiaryAccess();

  const { memberInformation, memberRole, selectedLegalBeneficiary } =
    useMemberInviteState();

  const isSubmitRequestLoading =
    submitInviteMemberRequestLoading || giveAdminPermissionsRequestLoading;

  const handleCancel = () => {
    history.push(routes.members);
  };

  const handleBackButtonClick = () => {
    goBack();
  };

  const role = memberRole.role ?? TeamMemberRole.Employee;

  const handleConfirm = async () => {
    try {
      if (selectedLegalBeneficiary && selectedLegalBeneficiary.companyUserId) {
        await giveLegalBeneficiaryAccess({
          companyUserId: selectedLegalBeneficiary.companyUserId,
          role,
        });
      } else {
        await submitInviteMemberRequest({
          ...memberInformation,
          role,
        });
      }
      history.push(routes.members);
      showToast({
        message: locales.formatString(locales.invitationConfirmation, {
          firstName: memberInformation.inviteeFirstName || '',
        }),
        type: 'success',
      });
    } catch (error) {
      const localizedError = getLocalizedError(error);
      showToast({
        message: localizedError,
        type: 'error',
      });
      logger.error('Could not invite additional team member', {
        context: { error },
      });
    }
  };

  return (
    <MemberReviewLayout
      confirmLabel={locales.confirmLabel}
      identity={{
        email: memberInformation.inviteeEmail,
        firstName: memberInformation.inviteeFirstName,
        lastName: memberInformation.inviteeLastName,
        role,
      }}
      isDisabled={isSubmitRequestLoading}
      isLoading={isSubmitRequestLoading}
      onApplyClicked={handleConfirm}
      onBackClicked={handleBackButtonClick}
      onCancelClicked={handleCancel}
      showDiff={false}
    />
  );
};

export default Review;
